import React from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { useBranding } from "@/lib/branding";

const POLICIES = {
    refund: {
        title: "Refund Policy",
        intro: "Cambridge College Online refunds tuition in accordance with the Private Training Act of British Columbia. This policy applies to all enrolled students regardless of residency tier.",
        sections: [
            {
                h: "Eligibility",
                body: "Refunds are issued in line with PTIB-mandated timelines, calculated from the program start date stated on the Letter of Acceptance. Students must submit a written withdrawal notice to receive a refund.",
            },
            {
                h: "Refund Schedule",
                body: "Before the program start date: full refund minus a non-refundable application fee (where applicable). Within 10% of program duration: partial refund as prescribed by PTIB. Beyond 30% of program duration: no refund unless required by law.",
            },
            {
                h: "How to Request",
                body: "Email contact@cambridgecollegeonline.com with your student ID, program name, and reason for withdrawal. Approved refunds are processed within 30 days of acceptance.",
            },
        ],
    },
    privacy: {
        title: "Privacy Policy",
        intro: "Cambridge College Online is committed to protecting the personal information of its students, applicants, and visitors in accordance with British Columbia's Personal Information Protection Act (PIPA).",
        sections: [
            {
                h: "Information We Collect",
                body: "We collect identifying information (name, date of birth, address), academic records, payment information, and learning-platform usage data necessary to deliver our programs and comply with PTIB recordkeeping requirements.",
            },
            {
                h: "How We Use It",
                body: "Information is used to manage enrolments, issue official documents, communicate program updates, process tuition payments, and meet our regulatory obligations. We do not sell or rent student data.",
            },
            {
                h: "Your Rights",
                body: "You may request access to your information, correct inaccuracies, or withdraw consent at any time by emailing contact@cambridgecollegeonline.com.",
            },
        ],
    },
    terms: {
        title: "Terms of Use",
        intro: "By using this website and student portal, you agree to the following terms. These terms govern your relationship with Cambridge College Online as a visitor, applicant, or enrolled student.",
        sections: [
            {
                h: "Acceptable Use",
                body: "You agree not to share your login credentials, attempt to access accounts other than your own, or use the platform for any unlawful purpose. Misuse may result in account suspension and academic disciplinary action.",
            },
            {
                h: "Intellectual Property",
                body: "All lesson content, assessments, and course materials are the intellectual property of Cambridge College Online and its licensors. You may not reproduce, distribute, or commercialize these materials without written permission.",
            },
            {
                h: "Limitation of Liability",
                body: "While we strive to keep the platform available 24/7, scheduled and unscheduled outages may occur. Cambridge College Online is not liable for indirect or consequential losses arising from platform unavailability.",
            },
        ],
    },
    conduct: {
        title: "Student Code of Conduct",
        intro: "Cambridge College Online expects every student to uphold standards of academic integrity and respectful conduct. This Code applies to all interactions on our learning platform, in virtual classrooms, and in college-related communications.",
        sections: [
            {
                h: "Academic Integrity",
                body: "Plagiarism, contract cheating, and unauthorized collaboration are strictly prohibited. All work submitted must be your own. Quiz attempts are monitored for integrity (tab-blur, copy/paste, and timing signals).",
            },
            {
                h: "Respectful Communication",
                body: "Harassment, discrimination, or harmful language directed at instructors, staff, or other students will not be tolerated. Violations may result in suspension or dismissal from the college.",
            },
            {
                h: "Reporting Concerns",
                body: "If you witness or experience a breach of this Code, contact the Director at contact@cambridgecollegeonline.com. Reports are handled confidentially.",
            },
        ],
    },
    dispute: {
        title: "Dispute Resolution Policy",
        intro: "Cambridge College Online is committed to addressing student concerns fairly, promptly, and confidentially. This policy outlines the steps for raising and resolving academic or administrative disputes.",
        sections: [
            {
                h: "Step 1 — Informal Resolution",
                body: "Where possible, raise your concern directly with the instructor or staff member involved within 10 business days of the issue. Most concerns are resolved at this stage.",
            },
            {
                h: "Step 2 — Formal Complaint",
                body: "If informal resolution is not possible, submit a written complaint to the Director at contact@cambridgecollegeonline.com, including your name, student ID, program, and a description of the issue and desired resolution. We will respond within 10 business days.",
            },
            {
                h: "Step 3 — External Review",
                body: "If you remain dissatisfied with the college's decision, you may escalate the matter to the Private Training Institutions Branch (PTIB) of British Columbia. PTIB contact details are available at privatetraininginstitutions.gov.bc.ca.",
            },
        ],
    },
};

export default function Policy() {
    const { slug } = useParams();
    const branding = useBranding();
    const policy = POLICIES[slug];

    if (!policy) {
        return (
            <div className="max-w-4xl mx-auto px-6 py-20" data-testid="policy-not-found">
                <h1 className="font-display text-3xl text-primary mb-4">Policy not found</h1>
                <Link to="/" className="text-accent hover:underline">← Back to home</Link>
            </div>
        );
    }

    return (
        <div className="bg-background" data-testid={`policy-${slug}`}>
            <section className="max-w-4xl mx-auto px-6 pt-16 pb-12">
                <Link to="/"
                      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-6">
                    <ChevronLeft className="h-4 w-4" /> Back to home
                </Link>
                <div className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-3">
                    {branding.institution_name || "Cambridge College Online"} · Policies
                </div>
                <h1 className="font-display text-4xl sm:text-5xl text-primary leading-[1.1] mb-6">
                    {policy.title}
                </h1>
                <p className="text-lg text-foreground/75 leading-relaxed">
                    {policy.intro}
                </p>
            </section>

            <section className="max-w-4xl mx-auto px-6 pb-20 space-y-8">
                {policy.sections.map((s, i) => (
                    <div key={i} className="border-l-2 border-accent/40 pl-6">
                        <h2 className="font-display text-xl text-primary mb-3">{s.h}</h2>
                        <p className="text-foreground/85 leading-relaxed">{s.body}</p>
                    </div>
                ))}

                <div className="mt-12 pt-8 border-t border-border text-sm text-muted-foreground">
                    <p>
                        This document is a working draft of Cambridge College Online's PTIB-compliant
                        policy. For the canonical, signed version, please contact{" "}
                        <a href="mailto:contact@cambridgecollegeonline.com"
                           className="text-primary hover:underline">
                            contact@cambridgecollegeonline.com
                        </a>.
                    </p>
                    <p className="mt-3">
                        Last reviewed: {new Date().toLocaleDateString("en-CA", {
                            year: "numeric", month: "long", day: "numeric",
                        })}
                    </p>
                </div>
            </section>
        </div>
    );
}
